import i18n from '@/utils/i18n';
import React, { useEffect, useMemo } from 'react';
import { AddPartitionItemsList } from '@/components/order-guide/add-partition-items-list';
import { createStyles } from '@/contexts/theme-context';
import { EDIT_ORDER_GUIDE_ROUTES } from '@/navigators/routes';
import { EditOrderGuideStackParams } from '@/navigators/types';
import { HEADER_PADDING_HORIZONTAL_SMALL } from '@/styles';
import { ItemSearchInput } from '@/components/item-search-input';
import { ModalContainer } from '@/components/modal-container';
import { NO_PARTITION_NAME } from '@/contexts/multi-order-guide-items-context-provider';
import {
  Position,
  ToastContextProvider,
  useToasts,
} from '@/contexts/toast-context';
import { SearchSortOrder } from '@/contexts/user-context';
import { StackScreenProps } from '@react-navigation/stack';
import { useEditOrderGuideState } from '@/contexts/edit-order-guide-context';
import { useSearchVariantPacks } from '@/hooks/use-search-variant-packs';

export type Props = StackScreenProps<
  EditOrderGuideStackParams,
  EDIT_ORDER_GUIDE_ROUTES.AddPartitionItems
>;

export const EditOrderGuideAddPartitionItemsScreen: React.FC<Props> = ({
  navigation,
  route,
}) => {
  const { clearSearch, items, searchItemsCallback, queryLoading } =
    useSearchVariantPacks({
      initialSearchText: route.params?.initialSearchText,
      searchSource: 'edit_order_guide',
    });
  const { partitions, selectedPartitionUUID } = useEditOrderGuideState();
  const { showErrorToast } = useToasts();
  const [styles] = useStyles();

  const selectedPartition = useMemo(() => {
    return partitions.find((p) => p.partitionUUID === selectedPartitionUUID);
  }, [partitions, selectedPartitionUUID]);

  useEffect(() => {
    if (!selectedPartition) {
      showErrorToast();
      navigation.goBack();
    }
  }, [navigation, selectedPartition, showErrorToast]);

  const navigationHeaderProps = useMemo(() => {
    return {
      showShadow: true,
      subheader: (
        <ItemSearchInput
          clearSearch={clearSearch}
          containerStyle={styles.searchInputContainer}
          initialSearchText={route.params?.initialSearchText}
          loading={queryLoading}
          onSearchItems={(query) =>
            searchItemsCallback(query, SearchSortOrder.Relevance)
          }
        />
      ),
      title: i18n.t('add_items_to_group', {
        groupName: selectedPartition?.partitionName || NO_PARTITION_NAME,
      }),
    };
  }, [
    clearSearch,
    queryLoading,
    route.params,
    searchItemsCallback,
    selectedPartition,
    styles,
  ]);

  return (
    <ModalContainer navigationHeaderProps={navigationHeaderProps}>
      {selectedPartition ? (
        <ToastContextProvider position={Position.BOTTOM}>
          <AddPartitionItemsList
            contentContainerStyle={styles.listContentContainer}
            items={items}
            selectedPartition={selectedPartition}
          />
        </ToastContextProvider>
      ) : null}
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  listContentContainer: {
    paddingHorizontal: 8,
  },
  searchInputContainer: {
    backgroundColor: theme.colors.background,
    marginBottom: HEADER_PADDING_HORIZONTAL_SMALL,
    marginHorizontal: HEADER_PADDING_HORIZONTAL_SMALL,
  },
}));
